/**
 * Boot splash — black canvas, dormant cold-blue arc-reactor core powers
 * up just long enough for the first paint to settle, then fades.
 *
 * No artificial duration. It exits as soon as the document is interactive
 * and the auth store has hydrated, which is the real "the app is ready"
 * signal. We just animate the fade so the transition reads as deliberate.
 */
import { useEffect, useState } from 'react';
import arcReactorIcon from '@/assets/arc-reactor-icon.png';

const SESSION_FLAG = 'jarvis_boot_splash_seen';

export default function BootSplash() {
  const alreadyShown =
    typeof window !== 'undefined' && sessionStorage.getItem(SESSION_FLAG) === '1';
  const [phase, setPhase] = useState<'cold' | 'warming' | 'gone'>(
    alreadyShown ? 'gone' : 'cold',
  );

  useEffect(() => {
    if (phase === 'gone') return;

    let cancelled = false;
    let warmTimer: ReturnType<typeof setTimeout> | null = null;
    let exitTimer: ReturnType<typeof setTimeout> | null = null;

    const exit = () => {
      if (cancelled) return;
      // Step into "warming" so the icon brightens before the fade.
      setPhase('warming');
      warmTimer = setTimeout(() => {
        if (cancelled) return;
        setPhase('gone');
        sessionStorage.setItem(SESSION_FLAG, '1');
      }, 700);
    };

    const ready = () => {
      // Defer to next frame to make sure first paint actually happened.
      requestAnimationFrame(() => {
        // Tiny floor so the dormant core is visible, not a flash.
        exitTimer = setTimeout(exit, 350);
      });
    };

    if (document.readyState === 'complete') {
      ready();
    } else {
      window.addEventListener('load', ready, { once: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener('load', ready);
      if (warmTimer) clearTimeout(warmTimer);
      if (exitTimer) clearTimeout(exitTimer);
    };
  }, [phase]);

  if (phase === 'gone') return null;

  const warming = phase === 'warming';

  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: warming ? 0 : 1,
        transition: 'opacity 600ms ease-out',
        pointerEvents: warming ? 'none' : 'auto',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: 140,
          height: 140,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: -40,
            borderRadius: '50%',
            background:
              'radial-gradient(circle, rgba(80,170,220,0.22) 0%, rgba(0,0,0,0) 70%)',
            opacity: warming ? 0.9 : 0.35,
            filter: warming ? 'blur(14px)' : 'blur(20px)',
            transition: 'opacity 700ms ease-out, filter 700ms ease-out',
          }}
        />
        <img
          src={arcReactorIcon}
          alt=""
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            opacity: warming ? 0.95 : 0.55,
            filter: warming
              ? 'brightness(1.1) saturate(1.05)'
              : 'brightness(0.55) saturate(0.5) hue-rotate(-10deg)',
            transition:
              'opacity 700ms ease-out, filter 700ms ease-out',
            animation: 'jarvisCoreBreath 3.6s ease-in-out infinite',
          }}
        />
      </div>
      <style>{`
        @keyframes jarvisCoreBreath {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.04); }
        }
      `}</style>
    </div>
  );
}
